import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Badge,
  Box,
  Chip,
  Divider,
  Grid,
  Typography,
} from '@mui/material';
import React, { Fragment, useEffect, useState } from 'react';
import { PrimaryButton } from '../../../../Buttons';
import {
  Brand,
  GeolocationLocationSet,
  GeolocationLocationSetData,
  IGeolocationSet,
} from '../../../../../types';
import GeolocationSetForm from './GeolocationSetForm';
import {
  createOrUpdateLocationSet,
  getBrandLocationSets,
} from '../../../../../services/assets';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { DEFAULT_COLOR_THEME } from '../../../../../utils';
import GeolocationList from './GeolocationList';
import { constantStringToHumanized } from '../../../../../utils/stringModifier';
import FacebookMap from './FacebookMap';
import { numberShortener } from '../../../../../utils/numberFormatter';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import { useDispatch } from 'react-redux';
import { toggleAlert } from '../../../../../redux/actions';
import { errorMessageParser } from '../../../../../utils/helpers/ToastHelper';
import CircularLoading from '../../../../CircularLoading';
import { NoData } from '../../../../../pages/NoContent';
import { HtmlTooltip } from '../../../../Tooltip';

interface GeolocationSetProps {
  brand: Brand;
}

const GeolocationSet: React.FC<GeolocationSetProps> = ({ brand }) => {
  const dispatch = useDispatch();
  const [isCreate, setIsCreate] = useState<boolean>(false);
  const [locationSets, setLocationSets] = useState<IGeolocationSet[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [fetchLoading, setFetchLoading] = useState<boolean>(false);
  const [_, setMap] = useState<any>(null);
  const [confirmArchive, setConfirmArchive] = useState<boolean>(false);
  const [editLocationSet, setEditLocationSet] = useState<boolean>(false);
  const [editLocationSetId, setEditLocationSetId] = useState<string>(null);

  useEffect(() => {
    if (brand && !isCreate) getLocationSets();
  }, [brand, isCreate]);

  const getLocationSets = async () => {
    try {
      setFetchLoading(true);

      const response = await getBrandLocationSets({
        brandId: brand?._id,
        archived: false,
      });

      const sortedResponse = response.data.sort((a: IGeolocationSet) => {
        if (a.primary) return -1;

        return 1;
      });

      setLocationSets(sortedResponse);
    } catch (error: any) {
      console.log(error);
    } finally {
      setFetchLoading(false);
    }
  };

  const isEmptyGeolocations = (geolocations: GeolocationLocationSet) => {
    return Object.values(geolocations).every(
      (field) => Array.isArray(field) && field.length === 0,
    );
  };

  const humanizeLocationTitle = (type: string) => {
    if (type === 'geo_markets') return 'DMAs';

    if (type === 'custom_locations') return 'Addresses';

    return constantStringToHumanized(type);
  };

  const handleOnArchive = async (locationSetId: string) => {
    try {
      setLoading(true);

      const response = await createOrUpdateLocationSet({
        brandId: brand?._id,
        body: { archived: true },
        locationSetId,
      });

      let temp: IGeolocationSet[] = locationSets.filter(
        (locationSet: IGeolocationSet) => locationSet._id !== response.data._id,
      );

      setLocationSets(temp);
      setConfirmArchive(false);

      dispatch(
        toggleAlert({
          toggle: true,
          message: 'Location set archived successfully',
          severity: 'success',
        }),
      );
    } catch (error: any) {
      const errorMsg = errorMessageParser(error);

      dispatch(
        toggleAlert({
          toggle: true,
          message: errorMsg,
          type: 'error',
        }),
      );
    } finally {
      setLoading(false);
    }
  };

  const handleOnCloseForm = () => {
    setIsCreate(false);
    setEditLocationSet(false);
    setEditLocationSetId(null);
  };

  const handleOnEdit = (locationSetId: string) => {
    setIsCreate(true);
    setEditLocationSet(true);
    setEditLocationSetId(locationSetId);
  };

  return isCreate ? (
    <GeolocationSetForm
      brand={brand}
      onClose={handleOnCloseForm}
      edit={editLocationSet}
      locationSetId={editLocationSetId}
    />
  ) : (
    <Grid container spacing={2}>
      <CircularLoading loading={fetchLoading} />

      <Grid item xs={12} sx={{ justifyContent: 'flex-end', display: 'flex' }}>
        <PrimaryButton
          title="Create Location Set"
          type="button"
          handleOnClick={() => setIsCreate(true)}
        />
      </Grid>

      <Grid item xs={12}>
        {locationSets.length > 0 ? (
          locationSets.map((locationSet: IGeolocationSet, index: number) => (
            <Accordion defaultExpanded={index === 0} key={locationSet._id}>
              <AccordionSummary
                expandIcon={<ExpandMoreIcon sx={{ color: 'white' }} />}
                aria-controls={`panel${index + 1}-content`}
                id={`panel${index + 1}-header`}
                sx={{ backgroundColor: DEFAULT_COLOR_THEME, color: 'white' }}
              >
                <Grid
                  container
                  spacing={2}
                  sx={{ alignItems: 'center', display: 'flex' }}
                >
                  <Grid item xs={12} sm={6}>
                    <Typography
                      component="span"
                      sx={{ width: '33%', flexShrink: 0, color: 'white' }}
                    >
                      {locationSet.name}
                    </Typography>

                    {locationSet.primary ? (
                      <Box component="span" ml={1}>
                        <Chip
                          label="Default"
                          size="small"
                          sx={{
                            color: DEFAULT_COLOR_THEME,
                            backgroundColor: 'white',
                            fontWeight: 'bold',
                          }}
                        />
                      </Box>
                    ) : null}
                  </Grid>

                  <Grid item xs={12} sm={4}>
                    <Typography component="span" sx={{ color: 'white' }}>
                      {`Est. Audience Size: ${numberShortener(
                        locationSet.minReach,
                      )} - ${numberShortener(locationSet.maxReach)}`}
                    </Typography>
                  </Grid>

                  <Grid
                    item
                    xs={12}
                    sm={2}
                    sx={{
                      flexDirection: 'row',
                      display: 'flex',
                      alignItems: 'center',
                    }}
                  >
                    <Typography component="span" sx={{ color: 'white' }}>
                      Usage:
                    </Typography>

                    <Badge
                      badgeContent={
                        <HtmlTooltip
                          disableInteractive
                          title={
                            <Fragment>
                              <>
                                <Typography
                                  variant="body2"
                                  sx={{ fontWeight: 'bold' }}
                                >
                                  Ads Usages
                                </Typography>

                                {locationSet.adsUsage?.map(
                                  (usage: any, index: number) => {
                                    return (
                                      <Box
                                        key={index + 1}
                                      >{`• ${usage.name} (${usage.id})`}</Box>
                                    );
                                  },
                                )}
                              </>
                            </Fragment>
                          }
                        >
                          {locationSet.usageCount as any}
                        </HtmlTooltip>
                      }
                      color="secondary"
                      sx={{
                        '& .MuiBadge-badge': {
                          right: -20,
                          backgroundColor: 'white',
                          color: DEFAULT_COLOR_THEME,
                          fontWeight: 'bold',
                        },
                      }}
                    />
                  </Grid>
                </Grid>
              </AccordionSummary>

              <AccordionDetails>
                <Grid container spacing={2} mb={1}>
                  <Grid item xs={12} sm={6}>
                    {locationSet?.description ? (
                      <Typography variant="body1">
                        {locationSet?.description}
                      </Typography>
                    ) : null}
                  </Grid>

                  <Grid
                    item
                    xs={12}
                    sm={6}
                    sx={{ justifyContent: 'flex-end', display: 'flex' }}
                  >
                    <PrimaryButton
                      title="Edit"
                      type="button"
                      startIcon={<EditOutlinedIcon />}
                      marginRight5
                      handleOnClick={() => handleOnEdit(locationSet._id)}
                    />

                    <PrimaryButton
                      title={confirmArchive ? 'Really archive?' : 'Archive'}
                      type="button"
                      startIcon={<DeleteOutlineOutlinedIcon />}
                      handleOnClick={() =>
                        confirmArchive
                          ? handleOnArchive(locationSet._id)
                          : setConfirmArchive(true)
                      }
                      theme={confirmArchive ? 'red' : DEFAULT_COLOR_THEME}
                      loading={loading}
                    />
                  </Grid>
                </Grid>

                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Grid container spacing={2}>
                      {!isEmptyGeolocations(locationSet.geolocations) ? (
                        <>
                          <Grid item xs={12}>
                            <Typography
                              variant="body1"
                              sx={{ fontWeight: 'bold', textAlign: 'center' }}
                            >
                              Included
                            </Typography>
                          </Grid>

                          <Grid item xs={12}>
                            <Divider />
                          </Grid>

                          {Object.keys(locationSet?.geolocations).map(
                            (key: string, index: number) => {
                              if (locationSet?.geolocations[key]?.length === 0)
                                return <Box key={`included-${key}-${index}`} />;

                              return (
                                <Grid
                                  item
                                  xs={12}
                                  key={`included-${key}-${index}`}
                                >
                                  <Grid container spacing={2}>
                                    <Grid item xs={12}>
                                      <Typography
                                        variant="body1"
                                        sx={{ fontWeight: 'bold' }}
                                      >
                                        {humanizeLocationTitle(key)}
                                      </Typography>
                                    </Grid>

                                    {locationSet?.geolocations[key].map(
                                      (
                                        geolocation: GeolocationLocationSetData,
                                        index: number,
                                      ) => {
                                        return (
                                          <Grid
                                            item
                                            xs={12}
                                            key={`included-${key}-${geolocation.key}-${index}`}
                                          >
                                            <GeolocationList
                                              geolocation={geolocation}
                                            />
                                          </Grid>
                                        );
                                      },
                                    )}
                                  </Grid>
                                </Grid>
                              );
                            },
                          )}
                        </>
                      ) : null}

                      {!isEmptyGeolocations(
                        locationSet.excludedGeolocations,
                      ) ? (
                        <>
                          <Grid item xs={12}>
                            <Typography
                              variant="body1"
                              sx={{ fontWeight: 'bold', textAlign: 'center' }}
                            >
                              Excluded
                            </Typography>
                          </Grid>

                          <Grid item xs={12}>
                            <Divider />
                          </Grid>

                          {Object.keys(locationSet?.excludedGeolocations).map(
                            (key: string, index: number) => {
                              if (
                                locationSet?.excludedGeolocations[key]
                                  ?.length === 0
                              )
                                return <Box key={`excluded-${key}-${index}`} />;

                              return (
                                <Grid
                                  item
                                  xs={12}
                                  key={`excluded-${key}-${index}`}
                                >
                                  <Grid container spacing={2}>
                                    <Grid item xs={12}>
                                      <Typography
                                        variant="body1"
                                        sx={{ fontWeight: 'bold' }}
                                      >
                                        {humanizeLocationTitle(key)}
                                      </Typography>
                                    </Grid>

                                    {locationSet?.excludedGeolocations[key].map(
                                      (
                                        geolocation: GeolocationLocationSetData,
                                        index: number,
                                      ) => {
                                        return (
                                          <Grid
                                            item
                                            xs={12}
                                            key={`excluded-${key}-${geolocation.key}-${index}`}
                                          >
                                            <GeolocationList
                                              geolocation={geolocation}
                                              exclude
                                            />
                                          </Grid>
                                        );
                                      },
                                    )}
                                  </Grid>
                                </Grid>
                              );
                            },
                          )}
                        </>
                      ) : null}
                    </Grid>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <FacebookMap
                          geolocations={locationSet?.geolocations}
                          excludedGeolocations={
                            locationSet?.excludedGeolocations
                          }
                          setParentMap={setMap}
                        />
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
              </AccordionDetails>
            </Accordion>
          ))
        ) : (
          <NoData />
        )}
      </Grid>
    </Grid>
  );
};

export default GeolocationSet;
